/**
 * CSVResultsModal Component
 * Modal dialog showing bulk import results with success/failure counts.
 * Displays failed users with error messages and allows exporting failed records.
 */

import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import { CheckmarkCircle24Filled, DismissCircle24Filled, ArrowDownload24Regular } from '@fluentui/react-icons';
import type { BulkUserResult } from '../../../types/user';

interface CSVResultsModalProps {
  isOpen: boolean;
  result: BulkUserResult | null;
  onClose: () => void;
}

const useStyles = makeStyles({
  summaryContainer: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalL),
    marginBottom: tokens.spacingVerticalL,
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  errorTableContainer: {
    maxHeight: '300px',
    overflowY: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    marginTop: tokens.spacingVerticalM,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase300,
  },
  th: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'left',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  errorCell: {
    color: tokens.colorPaletteRedForeground1,
    maxWidth: '300px',
    wordWrap: 'break-word',
  },
});

export function CSVResultsModal({ isOpen, result, onClose }: CSVResultsModalProps) {
  const styles = useStyles();

  if (!result) {
    return null;
  }

  const hasErrors = result.failed > 0;

  const handleExportFailures = () => {
    if (!result.errors || result.errors.length === 0) {
      return;
    }

    // Create CSV content with failed users
    const headers = ['userPrincipalName', 'error'];
    const rows = result.errors.map((error) => [
      error.user_principal_name,
      error.error_message.replace(/"/g, '""'), // Escape quotes
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `failed-users-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface data-testid="csv-results-modal">
        <DialogBody>
          <DialogTitle>Bulk Import Results</DialogTitle>
          <DialogContent>
            <div className={styles.summaryContainer}>
              <div className={styles.summaryItem}>
                <CheckmarkCircle24Filled className={styles.successIcon} />
                <Text weight="semibold">{result.successful} Succeeded</Text>
              </div>
              {hasErrors && (
                <div className={styles.summaryItem}>
                  <DismissCircle24Filled className={styles.errorIcon} />
                  <Text weight="semibold">{result.failed} Failed</Text>
                </div>
              )}
            </div>

            {!hasErrors && (
              <Text>All users were created successfully!</Text>
            )}

            {hasErrors && result.errors && result.errors.length > 0 && (
              <>
                <Text weight="semibold">Failed Users:</Text>
                <div className={styles.errorTableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>User Principal Name</th>
                        <th className={styles.th}>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((error, index) => (
                        <tr key={index}>
                          <td className={styles.td}>{error.user_principal_name}</td>
                          <td className={`${styles.td} ${styles.errorCell}`}>{error.error_message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {hasErrors && result.errors && result.errors.length > 0 && (
              <Button
                appearance="subtle"
                icon={<ArrowDownload24Regular />}
                onClick={handleExportFailures}
                data-testid="export-failures-button"
              >
                Export Failed Users
              </Button>
            )}
            <Button
              appearance="primary"
              onClick={onClose}
              data-testid="csv-results-close-button"
            >
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
